import api from "../axiosIntercepture";

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  (typeof err === "string" ? err : err?.message) ||
  fallback;

const normalizeListPayload = (raw) => {
  if (Array.isArray(raw)) return raw;
  return (
    raw?.data ??
    raw?.list ??
    raw?.results ??
    raw?.items ??
    raw?.records ??
    []
  );
};

/**
 * List customer groups for the current tenant.
 * @param {{ locationId?: string, status?: string }} [params] — optional locationId and status query params
 * @returns {Promise<Array>}
 */
export const fetchCustomerGroupList = async (params = {}) => {
  try {
    const { locationId, status } = params;
    const searchParams = new URLSearchParams();
    if (locationId) searchParams.set("locationId", String(locationId).trim());
    if (status && String(status).trim() !== "" && status !== "All") {
      searchParams.set("status", String(status).trim());
    }
    const qs = searchParams.toString();
    const url = qs ? `/customer-group/list?${qs}` : "/customer-group/list";
    const response = await api.get(url);
    const body = response.data;
    return normalizeListPayload(body);
  } catch (err) {
    throw getErrorMessage(err, "Failed to fetch customer groups");
  }
};

/**
 * Get one customer group (tenant-scoped).
 * @param {string} id
 * @returns {Promise<object>}
 */
export const getCustomerGroupById = async (id) => {
  try {
    const response = await api.get(`/customer-group/${id}`);
    const body = response.data;
    return body?.data ?? body?.result ?? body;
  } catch (err) {
    throw getErrorMessage(err, "Failed to load customer group");
  }
};

/**
 * Body shape expected by backend (createdBy is set server-side from auth user).
 */
const toCustomerGroupWritePayload = (data) => ({
  customerGroupCode: (data.customerGroupcode ?? data.customerGroupCode ?? data.code ?? "")
    .toString()
    .trim(),
  customerGroupName: (data.customerGroupName ?? data.name ?? "").toString().trim(),
  customerGroupDescription: (data.customerGroupDescription ?? data.description ?? "")
    .toString()
    .trim(),
  status: data.status || "Active",
});

/**
 * Create customer group (auth + server validation).
 */
export const createCustomerGroupAPI = async (data) => {
  try {
    const response = await api.post(
      "/customer-group/create",
      toCustomerGroupWritePayload(data)
    );
    return response.data;
  } catch (err) {
    throw getErrorMessage(err, "Failed to create customer group");
  }
};

/**
 * Update customer group (same field names as create; createdBy unchanged server-side unless backend overwrites).
 */
export const updateCustomerGroupAPI = async (data, id) => {
  try {
    const response = await api.post(
      `/customer-group/update/${id}`,
      toCustomerGroupWritePayload(data)
    );
    return response.data;
  } catch (err) {
    throw getErrorMessage(err, "Failed to update customer group");
  }
};


